import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { BoletoEligibilityResponse, BoletoExistsResponse } from '../services/api/boleto.service';
import BoletoConfirmationDialog from './BoletoConfirmationDialog';

interface BoletoButtonProps {
  invoiceId: string;
  className?: string;
  style?: React.CSSProperties;
  onBoletoCreated?: (boletoData: any) => void;
  onError?: (error: string) => void;
}

const BoletoButton: React.FC<BoletoButtonProps> = ({
  invoiceId,
  className = 'btn btn-warning',
  style,
  onBoletoCreated,
  onError
}) => {
  const [eligibility, setEligibility] = useState<BoletoEligibilityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState<BoletoExistsResponse | null>(null);

  
  const checkEligibility = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.checkBoletoEligibility(invoiceId);
      setEligibility(response);
    } catch (error: any) {
      console.error('Error checking boleto eligibility:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to check boleto eligibility';
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [invoiceId, onError]);

  useEffect(() => {
    checkEligibility();
  }, [checkEligibility]);

  const createBoleto = async (forceCreate: boolean = false) => {
    if (creating) return;
    setCreating(true);
    try {
      const response = await apiService.createBoletoForInvoice(invoiceId, undefined, forceCreate);
      
      if ((response as any)?.success) {
        alert('Boleto created successfully!');
        onBoletoCreated?.(response);
        // Refresh eligibility to show existing boleto
        await checkEligibility();
        // Close confirmation dialog if open
        setShowConfirmation(false);
        setConfirmationData(null);
      } else if ('requires_confirmation' in (response as any) && (response as any).requires_confirmation) {
        // Show confirmation dialog
        setConfirmationData(response as BoletoExistsResponse);
        setShowConfirmation(true);
      } else {
        throw new Error(response.message || 'Failed to create boleto');
      }
    } catch (error: any) {
      console.error('Error creating boleto:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create boleto';
      alert(`Error: ${errorMessage}`);
      onError?.(errorMessage);
    } finally {
      setCreating(false);
    }
  };
  
  const handleConfirmCreate = () => {
    createBoleto(true); // Force create new boleto
  };
  
  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setConfirmationData(null);
    setCreating(false);
  };

  if (loading) {
    return (
      <button className={className} style={style} disabled>
        Checking...
      </button>
    );
  }

  if (!eligibility?.eligible) {
    return (
      <button 
        className={`${className} disabled`} 
        style={{ ...style, opacity: 0.5 }} 
        disabled
        title={`Boleto not available: ${!eligibility?.currency_is_brl ? 'Currency must be BRL' : 'Customer must have tax ID'}`}
      >
        🇧🇷 Boleto N/A
      </button>
    );
  }

  if (eligibility?.existing_boleto) {
    return (
      <button 
        className={`${className.replace('btn-warning', 'btn-info')}`} 
        style={style}
        onClick={() => createBoleto(false)}
        disabled={creating}
        title={`Boleto exists (Status: ${eligibility.existing_boleto.status}). Click to create another one.`}
      >
        {creating ? 'Creating...' : '🇧🇷 Create New Boleto'}
      </button>
    );
  }

  return (
    <>
      <button
        className={className}
        style={style}
        onClick={() => createBoleto(false)}
        disabled={creating}
        title="Create boleto payment for this BRL invoice"
      >
        {creating ? 'Creating...' : '🇧🇷 Create Boleto'}
      </button>
      
      {showConfirmation && confirmationData && (
        <BoletoConfirmationDialog
          isOpen={showConfirmation}
          boletoData={confirmationData}
          onConfirm={handleConfirmCreate}
          onCancel={handleCancelConfirmation}
          loading={creating}
        />
      )}
    </>
  );
};

export default BoletoButton;